import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import { type FC, useEffect } from 'react';
// @ts-expect-error RHF v6 — FormBuilder uses legacy form API; v7 lives in `react-hook-form-latest` elsewhere
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { _notNil } from '@/littledash';

import type { FormBuilderFormData } from '@/components/FormBuilder/FormBuilder.model';
import {
  FieldTypes,
  groupFieldTypeOptions,
  groupTitleValue,
  showSelectField,
} from '@/components/FormBuilder/Item.utils';
import SelectBuilder from '@/components/FormBuilder/SelectBuilder/SelectBuilder';
import Checkbox from '@/components/UI/FormElements/Checkbox';
import ReactSelect from '@/components/UI/Select';

interface FormBuilderColumn {
  id: string;
  title: string;
  width: number;
}

interface FormBuilderItemProps {
  id: string;
  index: number;
  columns: Array<FormBuilderColumn>;
  onFieldTypeChange: (value: string) => void;
  type?: string;
  label?: string;
  hint?: string;
  options?: Array<{ label: string; value: string }>;
  required?: boolean;
}

const FormBuilderItem: FC<FormBuilderItemProps> = ({
  index,
  columns,
  onFieldTypeChange,
  type: typeFromProps,
  label: labelFromProps,
  hint: hintFromProps,
  options: optionsFromProps,
  required: requiredFromProps,
}) => {
  const { control, errors, register, unregister } = useFormContext<FormBuilderFormData>();

  const labelFieldName = `formItems[${index}].label`;
  const typeFieldName = `formItems[${index}].type`;
  const hintFieldName = `formItems[${index}].hint`;
  const optionsFieldName = `formItems[${index}].options`;
  const requiredFieldName = `formItems[${index}].required`;

  const hasLabelError = _notNil(errors?.formItems?.[index]?.label);
  const hasFieldTypeError = _notNil(errors?.formItems?.[index]?.type);

  const fieldTypesWithNoHintText = [groupTitleValue];
  const fieldTypesWithNoRequiredCheckbox = [groupTitleValue];

  const fieldType = useWatch({
    control,
    name: typeFieldName,
    defaultValue: typeFromProps,
  });

  const fieldLabel = useWatch({
    control,
    name: labelFieldName,
    defaultValue: labelFromProps,
  });

  const fieldHint = useWatch({
    control,
    name: hintFieldName,
    defaultValue: hintFromProps,
  });

  const fieldOptions = useWatch({
    control,
    name: optionsFieldName,
    defaultValue: optionsFromProps,
  });

  const fieldRequired = useWatch({
    control,
    name: requiredFieldName,
    defaultValue: requiredFromProps ?? true,
  });

  useEffect(() => {
    if (fieldType !== FieldTypes.select && fieldType !== FieldTypes.multi_select && fieldType !== FieldTypes.radio) {
      unregister(optionsFieldName);
    }
  }, [unregister, fieldType, optionsFieldName]);

  const defaultSelectValue = {
    label: 'Select...',
    value: '',
  };

  const getColumnById = (id: string): FormBuilderColumn | undefined => columns.find((column) => column.id === id);
  const getWidth = (id: string): number | undefined => getColumnById(id)?.width;

  const buildSelectStyles = (hasErrors: boolean) => ({
    control: (styles: Record<string, unknown>) => ({
      ...styles,
      border: hasErrors ? '2px solid red !important' : styles.border,
      '&:hover': {
        border: hasErrors ? '2px solid red !important' : styles.border,
      },
    }),
  });

  const getFieldTypeDefaultValue = (ft: string) =>
    ft === FieldTypes.radio
      ? groupFieldTypeOptions.find((option) => option.value === FieldTypes.select)
      : groupFieldTypeOptions.find((option) => option.value === ft);

  return (
    <div className="flex flex-row" data-test-component="FormBuilderItem" data-test-element="container">
      <div style={{ width: getWidth('fieldType') }} className="mr5">
        <div className="flex flex-column" data-test-element="field-type-select-container">
          <Controller
            defaultValue={
              fieldType
                ? (getFieldTypeDefaultValue(fieldType)?.value ?? defaultSelectValue.value)
                : defaultSelectValue.value
            }
            name={typeFieldName}
            rules={{
              required: 'This field is required',
            }}
            control={control}
            render={({ onChange, value }: { onChange: (v: string) => void; value: string | undefined }) => (
              <ReactSelect
                customStyles={buildSelectStyles(hasFieldTypeError)}
                value={value ? getFieldTypeDefaultValue(String(value)) : undefined}
                options={groupFieldTypeOptions}
                isMulti={false}
                onChange={(selected) => {
                  const next =
                    selected !== null && selected !== undefined && 'value' in selected
                      ? String((selected as { value: string }).value)
                      : '';
                  onFieldTypeChange(next);
                  onChange(next);
                }}
              />
            )}
          />
          <ErrorMessage
            errors={errors}
            name={typeFieldName}
            render={({ message }: { message: string }) => <p className="f6 red db pt2">{message}</p>}
          />
        </div>
      </div>
      <div style={{ width: getWidth('fieldProperties') }} className="mr5">
        {fieldType ? (
          <div>
            {showSelectField(fieldType) ? (
              <SelectBuilder
                names={{
                  label: labelFieldName,
                  options: optionsFieldName,
                }}
                defaultValue={{
                  label: fieldLabel ?? '',
                  options: fieldOptions ?? [{ label: '', value: '' }],
                }}
              />
            ) : (
              <div className="flex flex-column">
                <input
                  defaultValue={fieldLabel}
                  type="text"
                  data-test-element="label-field-input"
                  style={{ marginBottom: 0 }}
                  className={cn({ input__error: hasLabelError })}
                  name={labelFieldName}
                  ref={register({
                    required: 'This field is required',
                  })}
                  placeholder="Label"
                />
                <ErrorMessage
                  errors={errors}
                  name={labelFieldName}
                  render={({ message }: { message: string }) => <p className="f6 red db pt2">{message}</p>}
                />
              </div>
            )}
            {!fieldTypesWithNoHintText.includes(fieldType) && (
              <textarea
                defaultValue={fieldHint}
                name={hintFieldName}
                data-test-element="hint-field-textarea"
                ref={register({ maxLength: { value: 5000, message: 'Maximum of 5000 characters' } })}
                placeholder="Hint text (optional)"
                className="mt3"
                style={{ resize: 'vertical' }}
              />
            )}
            {!fieldTypesWithNoRequiredCheckbox.includes(fieldType) && (
              <Controller
                defaultValue={fieldRequired}
                name={requiredFieldName}
                control={control}
                render={({ onChange, value }: { onChange: (v: boolean) => void; value: boolean | undefined }) => (
                  <Checkbox
                    onChange={(e) => {
                      onChange(e.target.checked);
                    }}
                    label="Required"
                    name={requiredFieldName}
                    checked={value === true}
                  />
                )}
              />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default FormBuilderItem;
